import React, { useEffect, useRef, useState, useMemo } from "react";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { Button, Card, Typography, Divider, Row, Col, Avatar, Drawer, Form, Checkbox } from "antd";
import { PlusOutlined, DeleteFilled } from "@ant-design/icons";

//
import log from "../utils/log";
import c from "../utils/common";
import moment from "moment-timezone";
import dayjs from "dayjs";
import metaData from "../data/metaData";
import _ from "lodash";
import { style } from "../css/css";
import { useTranslation } from "react-i18next";

//
import SectionHeader from "./GetStarted/SectionHeader";
import Description from "./General/Description";
import Gallery from "./General/Gallery";
import Price from "./General/Price";
import Number from "./General/Number";
import AllianceShopTable from "./ShopTable";
import { formatTime } from "../utils/time";
import ItemSelectionDrawer from "./ItemSelectionDrawer";
import useAuthStore from "../states/useAuth";
import useShopQuery from "../hooks/useQuery/useShop";
import QtyDiscountPreview from "./Product/QtyDiscountPreview";
import PriceDiscountPreview from "./Product/PriceDiscountPreview";

const { Title, Text, Paragraph } = Typography;

const TermsDisplay = ({ state = {} }) => {
  const { data, form, onChange } = state;

  return data?.length > 0
    ? data.map((term, i) => (
        <div key={i} style={{ width: "100%", margin: "1rem 0" }}>
          <Row gutter={8}>
            <Col span={3}>
              <Avatar style={{ background: "var(--ion-color-primary-200)", color: "black" }}>{i + 1}</Avatar>
            </Col>
            <Col span={21} style={{ fontSize: "15px" }}>
              {term}
            </Col>
          </Row>
          {i == data.length - 1 ? null : <Divider style={{ margin: 6 }} />}
        </div>
      ))
    : null;
};

const ProductTypeDisplay = ({ state = {} }) => {
  const { t, i18n } = useTranslation();
  const { data, form, onChange } = state;

  // Use setFieldsValue to update the form values dynamically
  useEffect(() => {
    if (data?.type) {
      form.setFieldsValue({
        type: data.type,
      });
    }
    if (data?.discount?.type) {
      form.setFieldsValue({
        discount: { type: data.discount.type },
      });
    }
  }, [data, form]);

  return (
    <>
      <Title level={5}>Type</Title>
      <Text>{data?.type}</Text>

      {data?.type == "discount" ? (
        <>
          <Title level={5} style={{ marginTop: "2rem" }}>
            Discount Type
          </Title>
          <Text>{data?.discount?.type}</Text>
        </>
      ) : null}
    </>
  );
};

const ProductProfile = ({ state = {}, onChangeRedeemShopIds }) => {
  const { t, i18n } = useTranslation();
  const { data, form, onChange } = state;
  const { auth, user } = useAuthStore();

  const {
    data: allianceShops = [],
    isFetching: isFetchingAllianceShops,
    refetch: refetchAllianceShops,
  } = useShopQuery("shops/query", {
    $or: [
      { companyId: user?.companyId },
      { allianceIds: { $in: [user?.companyId] } }, // Check if companyId exists in allianceIds array
    ],
    select: "-gallery -paymentMethod -email -website -openHour -description -phone",
    populate: [
      {
        path: "company",
        select: "name",
      },
      {
        path: "brand",
        select: "name logo",
      },
    ],
  });

  const [isShowRedeemShopDrawer, setIsShowRedeemShopDrawer] = useState(false);

  // Handler to wrap data into the 'product' object
  const updateProductData = (data) => {
    log.debug(data);
    onChange({
      product: {
        ...data,
      },
    });
  };

  // Handler to wrap data into the 'product' object
  const handleTermsChange = (options) => {
    // log.debug(options);
    let updated;

    let existing = data?.terms?.length > 0 ? data.terms : [];
    // log.debug(existing);

    if (typeof options == "string") {
      updated = [...new Set([...existing, options])];
    } else {
      updated = [...new Set([...existing, ...options.filter((e) => e.isChecked).map((e) => e.value)])];
    }

    onChange({
      product: {
        terms: updated,
      },
    });
  };

  return (
    <Card
      // title={<SectionHeader title="Product" />}
      style={{
        border: "2px solid var(--ion-color-step-150)", // Change the color and width as needed
        position: "relative", // Enable positioning for the overlay
        overflow: "hidden", // Ensure the overlay doesn't exceed card boundaries
      }}
    >
      <ItemSelectionDrawer
        state={{
          data: data?.redeemShopIds,
          form: form,
          onChange: (shopIds, shops) => {
            updateProductData({
              redeemShopIds: shopIds,
              redeemShops: shops,
            });
          },
        }}
        header={
          <SectionHeader
            title="Select Redeem Shops"
            description={"select shops that can redeem this product"}
            style={{ margin: "0 0 0 1rem" }}
          />
        }
        items={allianceShops}
        isShow={isShowRedeemShopDrawer}
        tableType="shop"
        confirmBtnText={"Save"}
        onConfirm={() => {
          onChangeRedeemShopIds(data?.redeemShopIds);
          setIsShowRedeemShopDrawer(false);
        }}
        onClose={() => {
          setIsShowRedeemShopDrawer(false);
        }}
      />

      <Row>
        <Gallery
          state={{
            data: data?.gallery,
            onChange: updateProductData,
          }}
          aspect={1}
          readOnly={true}
          width={"15rem"}
          MAX={1}
          dots={false}
        />
        <Row style={{ width: "100%" }}>
          <Title level={4}>{data?.name}</Title>
        </Row>

        <Row style={{ width: "100%" }}>
          <Col span={6}>
            <Text type="secondary" style={{ marginRight: "2rem" }}>
              ID
            </Text>
          </Col>
          <Col>
            <Text>{data?.id}</Text>
          </Col>
        </Row>

        {(data?.type == "voucher" || data?.type == "giftCard") && !data?.isReferral && !data?.isHidePrice ? (
          <Row style={{ width: "100%" }}>
            <Col span={6}>
              <Text type="secondary" style={{ marginRight: "2rem" }}>
                Sale Period
              </Text>
            </Col>
            <Col>
              <Text>
                {data?.time?.sale
                  ? `${formatTime(data?.time?.sale?.start, "YYYY-MM-DD")} to ${formatTime(
                      data?.time?.sale?.end,
                      "YYYY-MM-DD"
                    )}`
                  : null}
              </Text>
            </Col>
          </Row>
        ) : null}

        {data?.type == "voucher" || data?.type == "discount" ? (
          <Row style={{ width: "100%" }}>
            <Col span={6}>
              <Text type="secondary" style={{ marginRight: "2rem" }}>
                Redeem Period
              </Text>
            </Col>
            <Col>
              <Text>
                {data?.time?.redeem
                  ? `${formatTime(data?.time?.redeem?.start, "YYYY-MM-DD")} to ${formatTime(
                      data?.time?.redeem?.end,
                      "YYYY-MM-DD"
                    )}`
                  : null}
              </Text>
            </Col>
          </Row>
        ) : null}

        <Divider style={{ background: "var(--ion-color-step-200)", height: "2px", margin: "3rem 0" }} />

        <Col span={8}>
          <ProductTypeDisplay
            state={{
              data: data,
              form: form,
              onChange: updateProductData,
            }}
          />

          {["voucher", "giftCard"].includes(data?.type) ||
          (data?.type === "discount" && data?.discount?.type === "price") ? (
            <Row align="middle" style={{ marginTop: "2rem" }}>
              <Title level={5}>{t("ticket.Value")}</Title>
              {data?.type === "voucher" && data?.isHidePrice ? (
                <Row style={{ width: "100%" }}>
                  <Text>Do not display</Text>
                </Row>
              ) : (
                <Row style={{ width: "100%" }}>
                  <Text style={{ fontSize: "1rem" }}>$ {data?.price}</Text>
                </Row>
              )}
            </Row>
          ) : null}

          {data?.discountMethod ? (
            <>
              <Title level={5} style={{ marginTop: "2rem" }}>
                Discount Method
              </Title>
              <Row>
                {data?.discountMethod?.trigger?.type == "qty" ? (
                  <QtyDiscountPreview
                    buy={data?.discountMethod?.trigger?.value}
                    free={data?.discountMethod?.reward?.value}
                  />
                ) : data?.discountMethod?.trigger?.type == "none" ? (
                  <PriceDiscountPreview price={data?.price} sellingPrice={data?.discountMethod?.reward?.value} />
                ) : null}
              </Row>
            </>
          ) : null}

          {data?.type == "discount" && data?.discount?.type == "percentage" ? (
            <Row align="middle" style={{ marginTop: "2rem" }}>
              <Row>
                <Title level={5}>{t("ticket.Percentage")}</Title>
              </Row>
              <Number
                state={{
                  data: data?.discount?.percentage,
                  form: form,
                  rules: [
                    { required: true, message: "required" },
                    {
                      validator: (_, value) =>
                        value > 0 ? Promise.resolve() : Promise.reject(new Error("Must be greater than zero")),
                    },
                  ],
                  onChange: (value) =>
                    value > 0 && updateProductData({ discount: { ...data?.discount, percentage: value } }),
                }}
                min={1}
                max={100}
                keyName="percentage"
                readOnly={true}
                style={{ inputFontSize: "18px" }}
                addonAfter={"% off"}
              />
            </Row>
          ) : null}

          {data?.type == "voucher" || data?.type == "takeaway" ? (
            <Row style={{ marginTop: "2rem" }}>
              <Title level={5}>{t("ticket.Amount")}</Title>
              <Row style={{ width: "100%" }}>
                <Text style={{ fontSize: "1rem" }}>{data?.qty?.total}</Text>
              </Row>
            </Row>
          ) : null}
        </Col>
      </Row>

      <Divider style={{ background: "var(--ion-color-step-200)", height: "2px", margin: "3rem 0" }} />

      <Title level={5} style={{ marginTop: "2rem" }}>
        Description
      </Title>
      <Description
        state={{
          data: data?.description,
          form: form,
          onChange: updateProductData,
          keyName: "description",
        }}
        readOnly={true}
        placeholder={"A brief description of your product"}
      />

      <Title level={5} style={{ marginTop: "2rem" }}>
        Terms
      </Title>
      <TermsDisplay
        state={{
          data: data?.terms,
          form: form,
          onChange: updateProductData,
        }}
      />

      <Divider style={{ background: "var(--ion-color-step-200)", height: "2px", margin: "3rem 0" }} />

      <SectionHeader
        title={
          <Title level={5} style={{ margin: "0" }}>
            Redeem Shop List
          </Title>
        }
      />

      <Card style={{ width: "25vw" }}>
        <Row justify="space-between" align="middle">
          <Title level={5}>Selected {data?.redeemShopIds?.length > 0 ? data?.redeemShopIds?.length : 0} Shops</Title>
          <Button
            size="large"
            type="primary"
            style={{ marginLeft: "2rem" }}
            onClick={() => {
              setIsShowRedeemShopDrawer(true);
            }}
          >
            Manage
          </Button>
        </Row>
      </Card>
    </Card>
  );
};

export default ProductProfile;
